import { useEffect, useRef } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import type { GeoJsonPolygon } from '@/utils/geojson';
import { useMapContext } from './MapContext';

export interface MapPolygonProps {
  polygon: GeoJsonPolygon;
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeWidth?: number;
  layerId?: string;
  sourceId?: string;
}

export function MapPolygon({
  polygon,
  fillColor = '#ff6b00',
  fillOpacity = 0.2,
  strokeColor = '#ff6b00',
  strokeWidth = 2,
  layerId = 'polygon-layer',
  sourceId = 'polygon-source',
}: MapPolygonProps) {
  const map = useMapContext();
  const sourceRef = useRef(sourceId);
  const layerRef = useRef(layerId);

  useEffect(() => {
    if (!map) {
      return;
    }

    const addSourceAndLayers = () => {
      if (!map.isStyleLoaded()) {
        return;
      }

      const source = map.getSource(sourceRef.current) as GeoJSONSource | undefined;
      if (source) {
        source.setData(polygon as GeoJSON.GeoJSON);
      } else {
        map.addSource(sourceRef.current, {
          type: 'geojson',
          data: polygon as GeoJSON.GeoJSON,
        });
      }

      if (!map.getLayer(layerRef.current)) {
        map.addLayer({
          id: layerRef.current,
          type: 'fill',
          source: sourceRef.current,
          paint: {
            'fill-color': fillColor,
            'fill-opacity': fillOpacity,
          },
        });

        map.addLayer({
          id: `${layerRef.current}-outline`,
          type: 'line',
          source: sourceRef.current,
          paint: {
            'line-color': strokeColor,
            'line-width': strokeWidth,
          },
        });
      }
    };

    if (map.isStyleLoaded()) {
      addSourceAndLayers();
    } else {
      const onStyleLoad = () => {
        addSourceAndLayers();
        map.off('styledata', onStyleLoad);
      };
      map.on('styledata', onStyleLoad);
      return () => map.off('styledata', onStyleLoad);
    }

    return () => {
      try {
        if (map.getLayer(layerRef.current)) {
          map.removeLayer(layerRef.current);
        }
        if (map.getLayer(`${layerRef.current}-outline`)) {
          map.removeLayer(`${layerRef.current}-outline`);
        }
        if (map.getSource(sourceRef.current)) {
          map.removeSource(sourceRef.current);
        }
      } catch {
        // map may already be destroyed
      }
    };
  }, [map, polygon, fillColor, fillOpacity, strokeColor, strokeWidth]);

  return null;
}
